import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, Copy, Check, Sparkles, Truck, Users, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const LAUNCH_OFFERS = [
  {
    id: "offer-firstgroom",
    code: "FIRSTGROOM",
    title: "20% Flat Discount on First Grooming",
    desc: "Get 20% flat discount on your pet's very first doorstep grooming appointment in Pune.",
    bgGradient: "from-amber-50 via-orange-50/40 to-amber-50",
    borderGradient: "from-amber-200/90 via-orange-300/80 to-amber-300/90",
    shadowColor: "shadow-amber-500/5",
    btnBg: "bg-woofsy-primary hover:bg-[#e8792c] text-white shadow-md",
    accentColor: "text-amber-600",
    image: "https://images.unsplash.com/photo-1516734212186-a967f81ad0d7?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "offer-nailtrim",
    code: "FREENAIL999",
    title: "Free Nail Trimming",
    desc: "Automatic complimentary nail clipping & smooth filing on all bookings above ₹999.",
    bgGradient: "from-emerald-50 via-teal-50/40 to-emerald-50",
    borderGradient: "from-emerald-200/90 via-teal-300/80 to-emerald-300/90",
    shadowColor: "shadow-emerald-500/5",
    btnBg: "bg-woofsy-primary hover:bg-[#e8792c] text-white shadow-md",
    accentColor: "text-emerald-600",
    image: "https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "offer-bandana",
    code: "FREEBANDANA",
    title: "Free Designer Pet Bandana",
    desc: "Complimentary washable designer Woofsy bandana with every Premium or Luxury package.",
    bgGradient: "from-purple-50 via-pink-50/40 to-purple-50",
    borderGradient: "from-purple-200/90 via-pink-300/80 to-purple-300/90",
    shadowColor: "shadow-purple-500/5",
    btnBg: "bg-woofsy-primary hover:bg-[#e8792c] text-white shadow-md",
    accentColor: "text-purple-600",
    image: "https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "offer-spaupgrade",
    code: "SPAHYDRO2499",
    title: "Free Spa Hydro Upgrade",
    desc: "Free hydro-massage & argan coat conditioning upgrade on bookings above ₹2,499.",
    bgGradient: "from-sky-50 via-blue-50/40 to-sky-50",
    borderGradient: "from-sky-200/90 via-blue-300/80 to-sky-300/90",
    shadowColor: "shadow-sky-500/5",
    btnBg: "bg-woofsy-primary hover:bg-[#e8792c] text-white shadow-md",
    accentColor: "text-sky-600",
    image: "https://images.unsplash.com/photo-1535294435445-d7249524ef2e?auto=format&fit=crop&w=800&q=80",
  },
];

export default function OffersSection({ onBook }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [copiedCode, setCopiedCode] = useState("");
  const [referralCopied, setReferralCopied] = useState(false);

  // Auto-rotate offers continuously every 3.5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % LAUNCH_OFFERS.length);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success(`Coupon "${code}" copied! Paste at booking.`);
    setTimeout(() => setCopiedCode(""), 3000);
  };

  const copyReferral = () => {
    const refText = "Hey! Use my Woofsy referral code WOOFSY200 to get ₹200 OFF on your first pet grooming booking in Pune! https://woofsy.in";
    navigator.clipboard.writeText(refText);
    setReferralCopied(true);
    toast.success("Referral message copied! Share with your friends.");
    setTimeout(() => setReferralCopied(false), 3000);
  };

  const nextOffer = () => setActiveIndex((prev) => (prev + 1) % LAUNCH_OFFERS.length);
  const prevOffer = () => setActiveIndex((prev) => (prev - 1 + LAUNCH_OFFERS.length) % LAUNCH_OFFERS.length);

  const activeOffer = LAUNCH_OFFERS[activeIndex];
  const isCopied = copiedCode === activeOffer.code;

  return (
    <section id="offers" data-testid="offers-section" className="relative py-16 sm:py-24 px-5 sm:px-8 bg-slate-50 scroll-mt-24">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-8">
          <span className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-woofsy-primary/10 text-woofsy-primary text-xs font-extrabold uppercase tracking-wider mb-2 border border-woofsy-primary/20">
            <Gift className="w-3.5 h-3.5" />
            <span>Exclusive Deals & Rewards</span>
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 mb-2">
            Woofsy Launch Offers & Referrals
          </h2>
          <p className="text-slate-600 text-sm sm:text-base font-medium">
            Enjoy instant discounts, complimentary pet gifts & refer friends to earn Woofsy wallet credits.
          </p>
        </motion.div>

        {/* Auto-Rotating Offer Banner Carousel (Max Width 4xl, Vibrant Glowing Gradient Ring Border) */}
        <div className="max-w-4xl mx-auto mb-8 relative">
          {/* Outer Glowing Gradient Ring Outer Container */}
          <div className={`p-[2.5px] rounded-[2.2rem] bg-gradient-to-r ${activeOffer.borderGradient} ${activeOffer.shadowColor} shadow-xl relative overflow-hidden transition-all duration-700`}>
            {/* Inner Content Card */}
            <div className={`rounded-[2rem] bg-gradient-to-br ${activeOffer.bgGradient} p-5 sm:p-7 backdrop-blur-xl relative overflow-hidden transition-all duration-700`}>
              
              {/* Ambient Background Blur Circle */}
              <div className="absolute -top-20 -right-20 w-56 h-56 bg-white/60 rounded-full blur-3xl pointer-events-none" />

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeOffer.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="grid md:grid-cols-12 gap-5 items-center relative z-10"
                >
                {/* Left Content */}
                <div className="md:col-span-7 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/90 border border-slate-200 shadow-2xs text-slate-700 font-extrabold text-[10px] uppercase tracking-wider mb-2 w-fit">
                    <Sparkles className={`w-3 h-3 ${activeOffer.accentColor}`} />
                    <span>Special Launch Deal</span>
                  </div>

                  <h3 className="font-heading font-black text-lg sm:text-xl text-slate-900 mb-1.5 leading-snug">
                    {activeOffer.title}
                  </h3>

                  <p className="text-slate-700 text-xs leading-relaxed mb-3.5 font-medium">
                    {activeOffer.desc}
                  </p>

                  <div className="flex flex-wrap items-center gap-2.5">
                    {/* Coupon Code Pill */}
                    <div className="p-1.5 px-3 rounded-xl bg-white border border-slate-200 flex items-center gap-2 shadow-xs">
                      <span className="font-mono font-black text-xs text-slate-900 tracking-wider">{activeOffer.code}</span>
                      <button
                        onClick={() => copyCode(activeOffer.code)}
                        data-testid={`copy-code-${activeOffer.code}`}
                        className="px-2 py-0.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-[10px] font-extrabold flex items-center gap-1 transition-colors border border-slate-200"
                      >
                        {isCopied ? <Check className="w-3 h-3 text-emerald-600 stroke-[3]" /> : <Copy className="w-3 h-3" />}
                        <span>{isCopied ? "Copied" : "Copy"}</span>
                      </button>
                    </div>

                    {/* Claim Offer CTA Button */}
                    <button
                      onClick={() => onBook("Full Grooming & Styling", activeOffer.code)}
                      className={`px-4 py-2 rounded-xl ${activeOffer.btnBg} font-heading font-extrabold text-xs shadow-sm transition-all flex items-center gap-1.5 transform hover:-translate-y-0.5`}
                    >
                      <span>Claim Offer Now</span>
                      <ArrowRight className="w-3 h-3 stroke-[2.5]" />
                    </button>
                  </div>
                </div>

                {/* Right Image Banner - Sleek High Quality Photo Display */}
                <div className="md:col-span-5">
                  <div className="relative h-48 sm:h-56 w-full rounded-2xl overflow-hidden shadow-md shadow-slate-900/10 border-2 border-white bg-white/80">
                    <img
                      src={activeOffer.image}
                      alt={activeOffer.title}
                      className="w-full h-full object-contain p-2 object-center"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/5 via-transparent to-transparent pointer-events-none" />
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows & Auto-timer Indicators */}
            <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-slate-900/10 relative z-10">
              {/* Dots Progress */}
              <div className="flex items-center gap-1.5">
                {LAUNCH_OFFERS.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveIndex(idx)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${activeIndex === idx ? "w-6 bg-slate-900" : "w-1.5 bg-slate-300 hover:bg-slate-400"
                      }`}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>

              {/* Prev / Next Controls */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={prevOffer}
                  className="w-7 h-7 rounded-lg bg-white/90 hover:bg-white text-slate-800 border border-slate-200 shadow-2xs flex items-center justify-center transition-all hover:scale-105"
                  aria-label="Previous offer"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={nextOffer}
                  className="w-7 h-7 rounded-lg bg-white/90 hover:bg-white text-slate-800 border border-slate-200 shadow-2xs flex items-center justify-center transition-all hover:scale-105"
                  aria-label="Next offer"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Referral Program Banner - Sniffynose Style Banner with Pure Clip-Path Sawtooth Cutouts (No White Outer Div) */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="w-full bg-gradient-to-r from-[#5a1864] via-[#6d1f79] to-[#491253] text-white p-7 sm:p-9 md:p-12 shadow-2xl mb-10 relative overflow-hidden"
          style={{
            clipPath: "polygon(0% 12px, 2% 0px, 4% 12px, 6% 0px, 8% 12px, 10% 0px, 12% 12px, 14% 0px, 16% 12px, 18% 0px, 20% 12px, 22% 0px, 24% 12px, 26% 0px, 28% 12px, 30% 0px, 32% 12px, 34% 0px, 36% 12px, 38% 0px, 40% 12px, 42% 0px, 44% 12px, 46% 0px, 48% 12px, 50% 0px, 52% 12px, 54% 0px, 56% 12px, 58% 0px, 60% 12px, 62% 0px, 64% 12px, 66% 0px, 68% 12px, 70% 0px, 72% 12px, 74% 0px, 76% 12px, 78% 0px, 80% 12px, 82% 0px, 84% 12px, 86% 0px, 88% 12px, 90% 0px, 92% 12px, 94% 0px, 96% 12px, 98% 0px, 100% 12px, 100% calc(100% - 12px), 98% 100%, 96% calc(100% - 12px), 94% 100%, 92% calc(100% - 12px), 90% 100%, 88% calc(100% - 12px), 86% 100%, 84% calc(100% - 12px), 82% 100%, 80% calc(100% - 12px), 78% 100%, 76% calc(100% - 12px), 74% 100%, 72% calc(100% - 12px), 70% 100%, 68% calc(100% - 12px), 66% 100%, 64% calc(100% - 12px), 62% 100%, 60% calc(100% - 12px), 58% 100%, 56% calc(100% - 12px), 54% 100%, 52% calc(100% - 12px), 50% 100%, 48% calc(100% - 12px), 46% 100%, 44% calc(100% - 12px), 42% 100%, 40% calc(100% - 12px), 38% 100%, 36% calc(100% - 12px), 34% 100%, 32% calc(100% - 12px), 30% 100%, 28% calc(100% - 12px), 26% 100%, 24% calc(100% - 12px), 22% 100%, 20% calc(100% - 12px), 18% 100%, 16% calc(100% - 12px), 14% 100%, 12% calc(100% - 12px), 10% 100%, 8% calc(100% - 12px), 6% 100%, 4% calc(100% - 12px), 2% 100%, 0% calc(100% - 12px))"
          }}
        >

          {/* Ambient Background Decorative Glows */}
          <div className="absolute -top-24 -left-24 w-80 h-80 bg-amber-400/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl pointer-events-none" />

          <div className="grid md:grid-cols-12 gap-6 items-center relative z-10 px-3 sm:px-5">

            {/* Left Column: Big Bold Text & Referral Code Action */}
            <div className="md:col-span-5 flex flex-col justify-center">
              <span className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-amber-400/20 border border-amber-300/30 text-amber-300 font-black text-[11px] uppercase tracking-wider mb-3 w-fit">
                <Users className="w-3.5 h-3.5 text-amber-400" />
                <span>Woofsy Referral Program</span>
              </span>

              <h3 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl text-white mb-2 leading-tight uppercase tracking-tight">
                REFER A PET PARENT, GET <span className="text-amber-400">₹200 CREDIT!</span>
              </h3>

              <p className="text-purple-100 text-xs sm:text-sm font-medium mb-5 leading-relaxed">
                Friends get <strong className="text-amber-300 font-extrabold">₹200 OFF</strong> their 1st grooming & you earn <strong className="text-amber-300 font-extrabold">₹200 Woofsy Cash!</strong>
              </p>

              <div className="flex flex-wrap items-center gap-3">
                {/* Code Pill */}
                <div className="px-3.5 py-2 rounded-xl bg-white/15 backdrop-blur-md border-2 border-dashed border-amber-300/70 font-mono text-base font-black tracking-widest text-amber-300 shadow-inner">
                  WOOFSY200
                </div>

                {/* Copy Button */}
                <button
                  onClick={copyReferral}
                  data-testid="btn-copy-referral"
                  className="px-5 py-2.5 rounded-xl bg-woofsy-primary hover:bg-[#e8792c] text-white font-heading font-black text-xs uppercase shadow-lg shadow-orange-500/25 transition-all flex items-center gap-1.5 transform hover:scale-105 active:scale-95"
                >
                  {referralCopied ? <Check className="w-4 h-4 text-white stroke-[3]" /> : <Copy className="w-4 h-4 stroke-[2.5]" />}
                  <span>{referralCopied ? "Copied!" : "Copy Link"}</span>
                </button>
              </div>
            </div>

            {/* Center Column: Pet Parent & Dog Photo */}
            <div className="md:col-span-4 flex justify-center">
              <div className="relative h-48 sm:h-56 w-full rounded-2xl overflow-hidden shadow-xl border-4 border-white/20 bg-purple-900/20">
                <img
                  src="https://images.unsplash.com/photo-1537151608828-ea2b11777ee8?auto=format&fit=crop&w=800&q=80"
                  alt="Refer a Pet Parent"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>

            {/* Right Column: Big Yellow Sniffynose Style Offer Badge */}
            <div className="md:col-span-3 flex justify-center md:justify-end">
              <div className="bg-gradient-to-tr from-amber-400 via-yellow-300 to-amber-400 text-slate-950 p-5 rounded-3xl font-black text-center shadow-2xl border-4 border-amber-200 transform hover:scale-105 transition-transform duration-300 w-full max-w-[220px]">
                <span className="block text-3xl font-black tracking-tight text-slate-950 mb-0.5">
                  ₹200 OFF
                </span>
                <span className="inline-block text-[11px] font-black tracking-wider uppercase px-2.5 py-0.5 rounded-full bg-slate-950 text-amber-300 mb-2">
                  INSTANT REWARD
                </span>
                <span className="block text-xs font-black text-slate-900 leading-tight">
                  Hurry!!! Refer Now &gt;&gt;
                </span>
              </div>
            </div>

          </div>
        </motion.div>

        {/* Doorstep Delivery Policy Banner - Soft Mint Light Theme */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="p-6 sm:p-7 rounded-[2rem] bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 border-2 border-teal-200 text-slate-900 shadow-lg shadow-teal-500/5 flex flex-col sm:flex-row items-center justify-between gap-6 relative overflow-hidden">

          <div className="flex items-center gap-4 relative z-10">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-teal-600 to-emerald-600 text-white flex items-center justify-center shrink-0 shadow-lg shadow-teal-600/25">
              <Truck className="w-6 h-6 stroke-[2.5]" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-extrabold text-teal-900 text-sm tracking-wide uppercase">Doorstep Service Guarantee</span>
                <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-ping" />
              </div>
              <p className="text-xs sm:text-sm text-slate-700 font-medium leading-relaxed">
                Minimum booking value <strong className="text-teal-900 font-extrabold">₹699</strong> for FREE mobile spa van doorstep delivery anywhere in Pune.
              </p>
            </div>
          </div>

          <button onClick={onBook} className="px-6 py-3.5 rounded-2xl bg-woofsy-primary hover:bg-[#e8792c] text-white font-heading font-black text-xs uppercase tracking-wider shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300 transform hover:-translate-y-0.5 shrink-0 flex items-center gap-2 relative z-10">
            <span>Book Doorstep Grooming</span>
            <ArrowRight className="w-4 h-4 stroke-[3]" />
          </button>
        </motion.div>

      </div>
    </section>
  );
}
